import { createServerFn } from "@tanstack/react-start";
import { publicSupabase } from "@/lib/supabase-public";
import { z } from "zod";
import { requireAuth as requireSupabaseAuth } from "@/lib/require-auth";
import { sanitizeBlogHtml } from "@/lib/sanitize-html";
import type { Database } from "@/integrations/supabase/types";
import { normalizeCredits, type Credit } from "@/lib/attribution";

async function ensureAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden");
}

export type BlogCategory = {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
};

export type BlogPost = {
  id: string;
  slug: string;
  url_prefix: string;
  path: string;
  title: string;
  excerpt: string | null;
  body_html: string;
  body_json: any;
  category_id: string | null;
  category_name: string | null;
  tags: string[];
  featured_image_id: string | null;
  featured_image_url: string | null;
  featured_image_alt: string | null;
  og_image_url: string | null;
  meta_title: string | null;
  meta_description: string | null;
  canonical_url: string | null;
  status: "draft" | "published";
  published_at: string | null;
  is_featured: boolean;
  read_time_minutes: number;
  view_count: number;
  author: string;
  authors: Credit[];
  contributors: Credit[];
  reviewers: Credit[];
  faq_items: Array<{ question: string; answer: string }>;
  source: "manual" | "interview";
  youtube_url: string | null;
  video_thumbnail_url: string | null;
  created_at: string;
  updated_at: string;
};

const POST_SELECT = `
  id, slug, url_prefix, title, excerpt, body_html, body_json, category_id,
  tags, featured_image_id, featured_image_alt, og_image_url, meta_title, meta_description,
  canonical_url, status, published_at, is_featured, read_time_minutes,
  view_count, author, authors, contributors, reviewers, faq_items, source, youtube_url, video_thumbnail_url, created_at, updated_at,
  blog_categories ( name ),
  media_assets!blog_posts_featured_image_id_fkey ( url, webp_url )
`;

function publicPath(urlPrefix: string, slug: string): string {
  return urlPrefix && urlPrefix !== "" ? `/${urlPrefix}/${slug}` : `/${slug}`;
}

function shapePost(row: any): BlogPost {
  const media = row.media_assets;
  return {
    id: row.id,
    slug: row.slug,
    url_prefix: row.url_prefix,
    path: publicPath(row.url_prefix, row.slug),
    title: row.title,
    excerpt: row.excerpt,
    // Sanitized on read as well as on write, so legacy rows are always safe and
    // the browser never needs to ship a sanitizer.
    body_html: sanitizeBlogHtml(row.body_html ?? ""),

    body_json: row.body_json,
    category_id: row.category_id,
    category_name: row.blog_categories?.name ?? null,
    tags: row.tags ?? [],
    featured_image_id: row.featured_image_id ?? null,
    featured_image_url: media?.webp_url ?? media?.url ?? null,
    featured_image_alt: row.featured_image_alt,
    og_image_url: row.og_image_url ?? media?.webp_url ?? media?.url ?? null,
    meta_title: row.meta_title,
    meta_description: row.meta_description,
    canonical_url: row.canonical_url,
    status: row.status,
    published_at: row.published_at,
    is_featured: row.is_featured,
    read_time_minutes: row.read_time_minutes,
    view_count: row.view_count,
    author: row.author,
    authors: normalizeCredits(row.authors),
    contributors: normalizeCredits(row.contributors),
    reviewers: normalizeCredits(row.reviewers),
    faq_items: Array.isArray(row.faq_items) ? row.faq_items : [],
    source: (row.source ?? "manual") as "manual" | "interview",
    youtube_url: row.youtube_url ?? null,
    video_thumbnail_url: row.video_thumbnail_url ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

async function publicClient() {
  const client = await publicSupabase();
  if (!client) throw new Error("Server not configured");
  return client;
}

/* ============ PUBLIC READS ============ */

export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z
      .object({
        page: z.number().int().min(1).default(1),
        pageSize: z.number().int().min(1).max(50).default(12),
        categoryId: z.string().optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const supabase = await publicClient();
    const from = (data.page - 1) * data.pageSize;
    const to = from + data.pageSize - 1;
    let q = supabase
      .from("blog_posts")
      .select(POST_SELECT, { count: "exact" })
      .eq("status", "published")
      .order("published_at", { ascending: false, nullsFirst: false })
      .range(from, to);
    if (data.categoryId) q = q.eq("category_id", data.categoryId);
    const { data: rows, count, error } = await q;
    if (error) throw new Error(error.message);
    return {
      posts: (rows ?? []).map(shapePost),
      total: count ?? 0,
      page: data.page,
      pageSize: data.pageSize,
    };
  });

export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().min(1) }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = await publicClient();
    const { data: row, error } = await supabase
      .from("blog_posts")
      .select(POST_SELECT)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const post = shapePost(row);
    // Related: 3 from same category, excluding self
    let related: BlogPost[] = [];
    if (post.category_id) {
      const { data: relRows } = await supabase
        .from("blog_posts")
        .select(POST_SELECT)
        .eq("status", "published")
        .eq("category_id", post.category_id)
        .neq("id", post.id)
        .order("published_at", { ascending: false })
        .limit(3);
      related = (relRows ?? []).map(shapePost);
    }
    return { post, related };
  });

export const listCategoriesWithCounts = createServerFn({ method: "GET" }).handler(
  async () => {
    const supabase = await publicClient();
    const [{ data: cats }, { data: posts }] = await Promise.all([
      supabase.from("blog_categories").select("*").order("sort_order"),
      supabase.from("blog_posts").select("category_id").eq("status", "published"),
    ]);
    const counts: Record<string, number> = {};
    for (const p of posts ?? []) {
      if (p.category_id) counts[p.category_id] = (counts[p.category_id] ?? 0) + 1;
    }
    return {
      categories: (cats ?? []).map((c) => ({
        ...c,
        count: counts[c.id] ?? 0,
      })) as Array<BlogCategory & { count: number }>,
      total: (posts ?? []).length,
    };
  },
);

export const getRecentPosts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ limit: z.number().int().min(1).max(20).default(5) }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const supabase = await publicClient();
    const { data: rows } = await supabase
      .from("blog_posts")
      .select(POST_SELECT)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(data.limit);
    return (rows ?? []).map(shapePost);
  });

export const getFeaturedPost = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = await publicClient();
  const { data: rows } = await supabase
    .from("blog_posts")
    .select(POST_SELECT)
    .eq("status", "published")
    .eq("is_featured", true)
    .order("published_at", { ascending: false })
    .limit(1);
  return rows && rows.length > 0 ? shapePost(rows[0]) : null;
});

export const listPostsByAuthor = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ author: z.string().min(1) }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = await publicClient();
    const { data: rows, error } = await supabase
      .from("blog_posts")
      .select(POST_SELECT)
      .eq("status", "published")
      .eq("author", data.author)
      .order("published_at", { ascending: false, nullsFirst: false });
    if (error) throw new Error(error.message);
    return (rows ?? []).map(shapePost);
  });

export const getCategoryById = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ id: z.string() }).parse(input))
  .handler(async ({ data }) => {
    const supabase = await publicClient();
    const { data: row } = await supabase
      .from("blog_categories")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    return row;
  });

/* ============ ADMIN WRITES ============ */

const PostInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(200),
  url_prefix: z.string().default("blog"),
  title: z.string().min(1).max(300),
  excerpt: z.string().max(500).nullable().optional(),
  body_html: z.string().default(""),
  body_json: z.any().optional(),
  category_id: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  featured_image_id: z.string().uuid().nullable().optional(),
  featured_image_alt: z.string().nullable().optional(),
  og_image_url: z.string().nullable().optional(),
  meta_title: z.string().max(120).nullable().optional(),
  meta_description: z.string().max(300).nullable().optional(),
  canonical_url: z.string().nullable().optional(),
  status: z.enum(["draft", "published"]).default("draft"),
  published_at: z.string().nullable().optional(),
  is_featured: z.boolean().default(false),
  read_time_minutes: z.number().int().min(1).default(3),
  author: z.string().max(200).nullable().optional(),
});

export const adminListPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select(POST_SELECT)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map(shapePost);
  });

export const adminGetPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .select(POST_SELECT)
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row ? shapePost(row) : null;
  });

export const adminUpsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PostInput.parse(input))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const payload: any = { ...data };
    if (typeof payload.body_html === "string") {
      payload.body_html = sanitizeBlogHtml(payload.body_html);
    }
    if (payload.status === "published" && !payload.published_at) {
      payload.published_at = new Date().toISOString();
    }
    const { data: row, error } = payload.id
      ? await context.supabase
          .from("blog_posts")
          .update(payload)
          .eq("id", payload.id)
          .select("id")
          .single()
      : await context.supabase.from("blog_posts").insert(payload).select("id").single();
    if (error) throw new Error(error.message);
    // NOTE: AI auto-draft generation intentionally disabled. Enable in Integrations.
    return { id: row.id };
  });

export const adminDeletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("blog_posts")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminDuplicatePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: original } = await context.supabase
      .from("blog_posts")
      .select("*")
      .eq("id", data.id)
      .single();
    if (!original) throw new Error("Not found");
    const copy: any = { ...original };
    delete copy.id;
    delete copy.created_at;
    delete copy.updated_at;
    copy.slug = `${original.slug}-copy-${Date.now().toString(36)}`;
    copy.title = `${original.title} (Copy)`;
    copy.status = "draft";
    copy.published_at = null;
    copy.is_featured = false;
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .insert(copy)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

/* ============ CONTENT DRAFTS ============ */

export type ContentDraft = {
  id: string;
  source_post_id: string | null;
  source_post_title: string | null;
  source_post_path: string | null;
  type: "gbp" | "social" | "stories";
  body: string;
  status: "draft" | "ready" | "posted";
  cta_type: string | null;
  cta_link: string | null;
  platform: string | null;
  attached_image_id: string | null;
  created_at: string;
};

export const adminListDrafts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        type: z.enum(["gbp", "social", "stories"]).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("content_drafts")
      .select(
        "id, source_post_id, type, body, status, cta_type, cta_link, platform, attached_image_id, created_at, blog_posts ( title, slug, url_prefix )",
      )
      .order("created_at", { ascending: false });
    if (data.type) q = q.eq("type", data.type);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r: any) => ({
      id: r.id,
      source_post_id: r.source_post_id,
      source_post_title: r.blog_posts?.title ?? null,
      source_post_path: r.blog_posts
        ? publicPath(r.blog_posts.url_prefix, r.blog_posts.slug)
        : null,
      type: r.type,
      body: r.body,
      status: r.status,
      cta_type: r.cta_type,
      cta_link: r.cta_link,
      platform: r.platform,
      attached_image_id: r.attached_image_id,
      created_at: r.created_at,
    })) as ContentDraft[];
  });

export const adminUpdateDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        body: z.string().optional(),
        status: z.enum(["draft", "ready", "posted"]).optional(),
        cta_type: z.string().nullable().optional(),
        cta_link: z.string().nullable().optional(),
        platform: z.string().nullable().optional(),
        attached_image_id: z.string().uuid().nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { id, ...patch } = data;
    const { error } = await context.supabase
      .from("content_drafts")
      .update(patch)
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminDeleteDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("content_drafts")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminCreateDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        type: z.enum(["gbp", "social", "stories"]),
        body: z.string().default(""),
        source_post_id: z.string().uuid().nullable().optional(),
        platform: z.string().nullable().optional(),
        cta_type: z.string().nullable().optional(),
        cta_link: z.string().nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("content_drafts")
      .insert({ ...data, status: "draft" })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const adminListAllCategories = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("blog_categories")
      .select("*")
      .order("sort_order");
    if (error) throw new Error(error.message);
    return data as BlogCategory[];
  });

export const adminRegenerateDrafts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ post_id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const fnUrl = `${process.env.SUPABASE_URL}/functions/v1/generate-post-content`;
    const res = await fetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Service role bearer — edge fn's requireAdmin treats this as a trusted
        // server-to-server call; we've already verified admin role above.
        Authorization: `Bearer ${process.env.SUPABASE_SERVICE_ROLE_KEY}`,
        apikey: process.env.SUPABASE_PUBLISHABLE_KEY!,
      },
      body: JSON.stringify({ post_id: data.post_id }),
    });
    if (!res.ok) throw new Error(`Generation failed: ${res.status}`);
    return { ok: true };
  });

/* All published posts for sitemap */
export const listAllPublishedForSitemap = createServerFn({ method: "GET" }).handler(
  async () => {
    const supabase = await publicClient();
    const { data } = await supabase
      .from("blog_posts")
      .select("slug, url_prefix, updated_at, published_at")
      .eq("status", "published");
    return (data ?? []).map((r) => ({
      path: publicPath(r.url_prefix, r.slug),
      lastmod: r.updated_at ?? r.published_at,
    }));
  },
);
